import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslations } from 'use-intl';

import { Button } from '@/shared-ui';

import ProjectUsabilityCameraSetup from './project-usability-camera-setup';

interface Props {
  projectId: string;
  showTabSelectionHint: boolean;
  onStuckClick: () => void;
  onContinue: () => void;
  isLoading?: boolean;
  showCameraSetup?: boolean;
}

const renderEmphasis = (chunks: React.ReactNode) => <strong>{chunks}</strong>;

export default function ProjectUsabilityHandsfreeWelcomeStep({
  projectId,
  showTabSelectionHint,
  onStuckClick,
  onContinue,
  isLoading = false,
  showCameraSetup = true,
}: Props) {
  const t = useTranslations('UserTest');

  return (
    <div className='flex flex-col gap-5 p-6'>
      <div className='flex flex-col gap-4'>
        <h2 className='text-lg font-semibold text-gray-900'>
          {t('preSession.beforeYouBeginTitle')}
        </h2>
        <p className='text-sm/relaxed text-gray-600'>
          {t('preSession.beforeYouBeginControls')}
        </p>
        <p className='text-sm/relaxed text-gray-600'>
          {t('preSession.beforeYouBeginSharing')}
        </p>
        {showTabSelectionHint && (
          <div className='flex gap-3 rounded-md bg-blue-50 p-4'>
            <FontAwesomeIcon
              icon={faCircleInfo}
              className='mt-0.5 size-5 shrink-0 text-blue-500'
              aria-hidden='true'
            />
            <div className='flex flex-col gap-1'>
              <p className='text-sm font-medium text-blue-900'>
                {t('preSession.tabHintTitle')}
              </p>
              <p className='text-xs text-blue-700'>
                {t.rich('preSession.tabHintBody', {
                  emphasis: renderEmphasis,
                })}
              </p>
            </div>
          </div>
        )}
      </div>

      {showCameraSetup && <ProjectUsabilityCameraSetup projectId={projectId} />}

      <div className='flex w-full gap-2'>
        <Button
          onClick={onStuckClick}
          variant='secondary'
          size='md'
          className='flex-1 rounded-full bg-gray-200 text-gray-900 hover:enabled:bg-gray-200'
          disabled={isLoading}
          data-test='handsfree-welcome-stuck-button'
        >
          {t('stuck.title')}
        </Button>
        <Button
          onClick={onContinue}
          variant='primary'
          size='md'
          className='flex-1 rounded-full bg-gray-900 shadow-[0_4px_24px_0_rgba(52,52,111,0.24)] hover:bg-gray-800'
          disabled={isLoading}
          data-test='handsfree-welcome-continue-button'
        >
          {isLoading
            ? t('preSession.waitingForPermissions')
            : t('preSession.continue')}
        </Button>
      </div>
    </div>
  );
}
